/* ===========================================================
   Lucraa — Componentes compartilhados
   =========================================================== */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { fmt } from "../domain/format";

/* ---------- Logo / marca ---------- */
export function LucraaMark({ size = 34, tone = "var(--lucro)" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <rect x="1.5" y="1.5" width="37" height="37" rx="11" fill={tone} />
      {/* trilha ascendente — "ver o lucro subir" */}
      <path
        d="M9 27 L17 19 L23 23 L31 12"
        stroke="#fff"
        strokeWidth="3.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="31" cy="12" r="3.3" fill="var(--mint)" stroke={tone} strokeWidth="2" />
      <path d="M9 31.5 H31" stroke="rgba(255,255,255,.35)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
export function Logo({ size = 34, color = "var(--ink)", compact = false }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <LucraaMark size={size} />
      {!compact && (
        <span
          className="font-display"
          style={{ fontWeight: 700, fontSize: size * 0.74, color, letterSpacing: "-0.03em" }}
        >
          Lucraa
        </span>
      )}
    </span>
  );
}

/* ---------- Ícones (stroke, 24x24) ---------- */
export const IconPaths = {
  home: (
    <>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9.5h13V10" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </>
  ),
  bolt: <path d="M13 3 5 13h6l-1 8 8-10h-6l1-8Z" />,
  coins: (
    <>
      <ellipse cx="9" cy="7" rx="6" ry="3" />
      <path d="M3 7v5c0 1.66 2.7 3 6 3s6-1.34 6-3V7" />
      <path d="M15 11.2c2.4.4 6 1.6 6 3.3 0 1.66-2.7 3-6 3-1.2 0-2.3-.18-3.2-.48" />
    </>
  ),
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  arrowUp: (
    <>
      <path d="M12 19V5" />
      <path d="m6 11 6-6 6 6" />
    </>
  ),
  arrowDown: (
    <>
      <path d="M12 5v14" />
      <path d="m6 13 6 6 6-6" />
    </>
  ),
  check: <path d="m4 12.5 5 5L20 6.5" />,
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
  spark: (
    <>
      <path
        d="M12 2 Q13 11 22 12 Q13 13 12 22 Q11 13 2 12 Q11 11 12 2 Z"
        fill="currentColor"
        stroke="none"
      />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v4M12 17.5v.5" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2.5" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  cart: (
    <>
      <circle cx="9" cy="20" r="1.6" />
      <circle cx="18" cy="20" r="1.6" />
      <path d="M2 3h3l2.4 12.2a1.5 1.5 0 0 0 1.5 1.2h8.3a1.5 1.5 0 0 0 1.5-1.2L21 7H6" />
    </>
  ),
  box: (
    <>
      <path d="m3.5 7 8.5-4 8.5 4-8.5 4-8.5-4Z" />
      <path d="M3.5 7v10l8.5 4 8.5-4V7" />
      <path d="M12 11v10" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  chart: (
    <>
      <path d="M4 4v16h16" />
      <path d="M7 14l3-3 3 2 5-6" />
    </>
  ),
  store: (
    <>
      <path d="M4 9h16l-1-5H5L4 9Z" />
      <path d="M5 9v10h14V9" />
      <path d="M9 19v-5h6v5" />
    </>
  ),
  link: (
    <>
      <path d="M9 15 15 9" />
      <path d="M11 6.5 13 4.5a4 4 0 0 1 5.7 5.7l-2 2" />
      <path d="M13 17.5 11 19.5a4 4 0 0 1-5.7-5.7l2-2" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  x: (
    <>
      <path d="M6 6l12 12M18 6 6 18" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18" />
    </>
  ),
  play: <path d="M8 5v14l11-7-11-7Z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" />
      <circle cx="15" cy="6" r="2" fill="var(--canvas)" />
      <circle cx="8" cy="12" r="2" fill="var(--canvas)" />
      <circle cx="13" cy="18" r="2" fill="var(--canvas)" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.6v3M12 18.4v3M21.4 12h-3M5.6 12h-3M18.36 5.64l-2.12 2.12M7.76 16.24l-2.12 2.12M18.36 18.36l-2.12-2.12M7.76 7.76 5.64 5.64" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.8 19.2a6.2 6.2 0 0 1 12.4 0" />
      <path d="M16.2 5.2a3.4 3.4 0 0 1 0 6.6" />
      <path d="M17.2 13.4a6.2 6.2 0 0 1 4 5.8" />
    </>
  ),
  logout: (
    <>
      <path d="M14 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H14" />
      <path d="M18 12H10" />
      <path d="m15 9 3 3-3 3" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7.5 8 5.5 8-5.5" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V5.6A1.6 1.6 0 0 1 10.6 4h2.8A1.6 1.6 0 0 1 15 5.6V7" />
      <path d="M6.2 7 7.1 19.4A1.6 1.6 0 0 0 8.7 21h6.6a1.6 1.6 0 0 0 1.6-1.6L17.8 7" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  chevronLeft: <path d="m14.5 6-6 6 6 6" />,
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  calendar: (
    <>
      <path d="M4.5 6.5A1.5 1.5 0 0 1 6 5h12a1.5 1.5 0 0 1 1.5 1.5V18A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18V6.5Z" />
      <path d="M4.5 9.5h15" />
      <path d="M8.5 3.5v3M15.5 3.5v3" />
    </>
  ),
  megaphone: (
    <>
      <path d="M3 11v2a1.5 1.5 0 0 0 1.5 1.5H7l9 4.5V5L7 9.5H4.5A1.5 1.5 0 0 0 3 11Z" />
      <path d="M19.5 9.5a3.5 3.5 0 0 1 0 5" />
      <path d="M8 15v4a1.5 1.5 0 0 0 1.5 1.5h1A1.5 1.5 0 0 0 12 19v-3" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v10" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15V5" />
      <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
      <path d="M4.5 19.5h15" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 6v5h-5" />
      <path d="M4 18v-5h5" />
      <path d="M19.5 11a7.6 7.6 0 0 0-13.1-3.6L4 9.8M4.5 13a7.6 7.6 0 0 0 13.1 3.6l2.4-2.4" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M19 14v5a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 4 19V7a1.5 1.5 0 0 1 1.5-1.5H10" />
    </>
  ),
  sheet: (
    <>
      <path d="M6 2.8h8.5L19 7.3V21.2H6V2.8Z" />
      <path d="M14 3v4.5h4.5" />
      <path d="M8.5 12h7M8.5 15.5h7M8.5 19h4" />
    </>
  ),
  repeat: (
    <>
      <path d="M17 2.5 21 6.5l-4 4" />
      <path d="M3 11V9.5a3 3 0 0 1 3-3h15" />
      <path d="M7 21.5 3 17.5l4-4" />
      <path d="M21 13v1.5a3 3 0 0 1-3 3H3" />
    </>
  ),
  dots: (
    <>
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  whatsapp: (
    <>
      <path d="M12 4a8 8 0 0 0-6.9 12L4 20l4.2-1A8 8 0 1 0 12 4Z" />
      <path d="M9.3 9.5c.4 2.4 2.8 4.8 5.2 5.2l1-1.5-2-1-.8.7c-.7-.4-1.2-.9-1.6-1.6l.7-.8-1-2-1.5 1Z" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.3 9.2a2.8 2.8 0 0 1 5.3 1c0 1.9-2.6 2-2.6 3.8" />
      <path d="M12 17.3v.4" />
    </>
  ),
};
export function Icon({ name, size = 22, color = "currentColor", strokeWidth = 1.9, style = {} }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block", ...style }}
    >
      {IconPaths[name]}
    </svg>
  );
}

/* ---------- Logos das integrações (placeholders estilizados) ---------- */
export function IntegrationGlyph({ kind, size = 44 }) {
  const wrap = (bg, child) => (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 12,
        background: bg,
        display: "grid",
        placeItems: "center",
        flex: "none",
      }}
    >
      {child}
    </div>
  );
  if (kind === "nuvemshop")
    return wrap(
      "#2B2A4A",
      <svg width={size * 0.56} height={size * 0.56} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 16a4 4 0 0 1 .5-7.97A5 5 0 0 1 16 7a3.5 3.5 0 0 1 2 6.6"
          stroke="#fff"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
        <path d="M7 16h11" stroke="#7CF2C0" strokeWidth="1.9" strokeLinecap="round" />
      </svg>,
    );
  if (kind === "ga")
    return wrap(
      "#E8743B",
      <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="11" width="4.5" height="9" rx="2" fill="#fff" />
        <rect x="9.7" y="6" width="4.5" height="14" rx="2" fill="#fff" />
        <rect x="16.4" y="3" width="4.5" height="17" rx="2" fill="#FBD9C6" />
      </svg>,
    );
  if (kind === "gads")
    return wrap(
      "#1A73E8",
      <svg width={size * 0.54} height={size * 0.54} viewBox="0 0 24 24" fill="none">
        <path d="M5 19 11 7l4 2-6 12-4-2Z" fill="#fff" />
        <circle cx="17" cy="16" r="3.4" fill="#FFD24D" />
      </svg>,
    );
  if (kind === "meta")
    return wrap(
      "#1D6FE0",
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
        <path
          d="M3 16c1.5-7 4-9 5.5-9 2.2 0 3 5 3.5 5s1.3-5 3.5-5C20 7 22 9 21 16"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>,
    );
  if (kind === "olist")
    return wrap(
      "#2E2A3D",
      <svg width={size * 0.54} height={size * 0.54} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3 4 7v10l8 4 8-4V7l-8-4Z"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path d="M4 7l8 4 8-4M12 11v10" stroke="#7CF2C0" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>,
    );
  if (kind === "mercadolivre")
    return wrap(
      "#FFE600",
      <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 24 24" fill="none">
        <path
          d="M4 10c3 5.5 13 5.5 16 0"
          stroke="#2D3277"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle cx="8.5" cy="13" r="1.05" fill="#2D3277" />
        <circle cx="15.5" cy="13" r="1.05" fill="#2D3277" />
      </svg>,
    );
  return null;
}

/* ---------- Tooltip de ajuda ---------- */
export function HelpTip({
  text,
  title = "",
  size = 15,
  color = "var(--ink-soft)",
  label = "Ajuda",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, arrow: 134 });
  const ref = useRef(null);
  const W = 272;

  function place() {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.left + r.width / 2 - W / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - W - 12));
    setPos({ top: r.bottom + 10, left, arrow: r.left + r.width / 2 - left });
  }
  function show() {
    place();
    setOpen(true);
  }
  function hide() {
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const re = () => place();
    window.addEventListener("scroll", re, true);
    window.addEventListener("resize", re);
    return () => {
      window.removeEventListener("scroll", re, true);
      window.removeEventListener("resize", re);
    };
  }, [open]);

  return (
    <span ref={ref} style={{ display: "inline-flex" }} onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          open ? hide() : show();
        }}
        onFocus={show}
        onBlur={hide}
        style={{
          display: "grid",
          placeItems: "center",
          width: size + 5,
          height: size + 5,
          padding: 0,
          borderRadius: 99,
          border: "none",
          cursor: "pointer",
          background: "transparent",
          color,
          lineHeight: 0,
          transition: "color .15s",
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Icon name="help" size={size} color={color} strokeWidth={1.9} />
      </button>
      {open &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: W,
              zIndex: 9999,
              background: "var(--ink)",
              color: "#fff",
              borderRadius: 13,
              padding: "15px 17px",
              boxShadow: "0 16px 38px rgba(17,32,26,.26)",
              fontFamily: "var(--font-body)",
              textAlign: "left",
              animation: "helptip-in .14s ease both",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -5,
                left: Math.max(14, Math.min(pos.arrow, W - 22)) - 5,
                width: 11,
                height: 11,
                background: "var(--ink)",
                transform: "rotate(45deg)",
                borderRadius: 2,
              }}
            />
            {title && (
              <div
                className="font-display"
                style={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  marginBottom: 6,
                  color: "var(--mint)",
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </div>
            )}
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: "rgba(255,255,255,.92)",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              {text}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}

/* ---------- Delta ---------- */
export function Delta({ value, suffix = "vs. mês anterior", warn = false, size = 13 }) {
  const down = value < 0;
  const cls = warn ? "delta-warn" : down ? "delta-down" : "delta-up";
  const arrow = down ? "▼" : "▲";
  return (
    <span className={"delta " + cls} style={{ fontSize: size }}>
      <span style={{ fontSize: size * 0.82 }}>{arrow}</span>
      {fmt.pct(value)}
      {suffix && (
        <span style={{ color: "var(--ink-soft)", fontWeight: 500, marginLeft: 2 }}>{suffix}</span>
      )}
    </span>
  );
}

/* ---------- Card-resposta (componente assinatura) ---------- */
export function ResponseCard({
  icon = "spark",
  frase,
  valor,
  valorTom = "lucro",
  sub,
  acao,
  onAcao,
  compact = false,
}) {
  const tomColor =
    valorTom === "prejuizo"
      ? "var(--prejuizo)"
      : valorTom === "alerta"
        ? "var(--alerta)"
        : "var(--lucro)";
  const wash =
    valorTom === "prejuizo"
      ? "var(--prejuizo-wash)"
      : valorTom === "alerta"
        ? "var(--alerta-wash)"
        : "var(--mint-wash)";
  return (
    <div
      className="card"
      style={{
        padding: compact ? 20 : 24,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        height: "100%",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: wash,
            display: "grid",
            placeItems: "center",
            flex: "none",
          }}
        >
          <Icon name={icon} size={20} color={tomColor} />
        </div>
        <p
          className="font-display"
          style={{
            fontSize: compact ? 17 : 19,
            fontWeight: 600,
            lineHeight: 1.25,
            margin: 0,
            color: "var(--ink)",
          }}
        >
          {frase}
        </p>
      </div>
      {valor != null && (
        <div
          className="num"
          style={{
            fontSize: compact ? 28 : 34,
            color: tomColor,
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {valor}
          {sub && (
            <span
              style={{ fontSize: 14, color: "var(--ink-soft)", fontWeight: 500, marginLeft: 8 }}
            >
              {sub}
            </span>
          )}
        </div>
      )}
      {acao && (
        <button
          onClick={onAcao}
          className="link-acao"
          style={{
            marginTop: "auto",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--lucro)",
            fontWeight: 600,
            fontSize: 14.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "var(--font-body)",
          }}
        >
          {acao} <Icon name="arrowRight" size={16} />
        </button>
      )}
    </div>
  );
}

/* ---------- KPI hero ---------- */
export function KpiCard({
  label,
  valor,
  delta,
  deltaWarn,
  tom = "ink",
  hero = false,
  foot,
  help,
  helpTitle,
}) {
  const color =
    tom === "lucro" ? "var(--lucro)" : tom === "alerta" ? "var(--alerta)" : "var(--ink)";
  return (
    <div
      className="card"
      style={{
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        position: "relative",
        borderColor: hero ? "rgba(14,140,90,.35)" : "var(--linha)",
        boxShadow: hero
          ? "0 1px 2px rgba(17,32,26,.06), 0 0 0 1px rgba(14,140,90,.12)"
          : "var(--shadow-paper)",
      }}
    >
      {hero && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "var(--lucro)",
            borderRadius: "var(--radius, 16px) var(--radius, 16px) 0 0",
          }}
        />
      )}
      <span
        className="label-eyebrow"
        style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
      >
        {label}
        {help && <HelpTip text={help} title={helpTitle} />}
      </span>
      <div
        className="num"
        style={{ fontSize: hero ? 52 : 38, color, lineHeight: 1, letterSpacing: "-0.03em" }}
      >
        {valor}
      </div>
      {delta != null && <Delta value={delta} warn={deltaWarn} />}
      {foot && <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 2 }}>{foot}</div>}
    </div>
  );
}

/* ---------- Sparkline / linha de lucro (SVG) ---------- */
export function LineChart({
  data,
  w = 760,
  h = 200,
  pad = 18,
  color = "var(--lucro)",
  fill = true,
  showDots = false,
  prefix = "R$ ",
  scale = 1,
}) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const xs = data.map((d) => d.dia ?? d.x);
  const ys = data.map((d) => d.valor ?? d.y);
  const minY = Math.min(...ys),
    maxY = Math.max(...ys);
  const minX = Math.min(...xs),
    maxX = Math.max(...xs);
  const sx = (v) => pad + ((v - minX) / (maxX - minX || 1)) * (w - pad * 2);
  const sy = (v) => h - pad - ((v - minY) / (maxY - minY || 1)) * (h - pad * 2.2);
  const pts = data.map((d) => [sx(d.dia ?? d.x), sy(d.valor ?? d.y)]);
  const line = pts
    .map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1))
    .join(" ");
  const area =
    line +
    ` L ${pts[pts.length - 1][0].toFixed(1)} ${h - pad} L ${pts[0][0].toFixed(1)} ${h - pad} Z`;

  function move(e) {
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * w;
    let best = 0,
      bd = 1e9;
    pts.forEach((p, i) => {
      const d = Math.abs(p[0] - x);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    setHover(best);
  }
  const gid = "lg" + Math.round(minY);
  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      style={{ display: "block", overflow: "visible" }}
      onMouseMove={move}
      onMouseLeave={() => setHover(null)}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--mint)" stopOpacity="0.45" />
          <stop offset="1" stopColor="var(--mint)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={area} fill={`url(#${gid})`} />}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDots &&
        pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2" fill={color} opacity="0.5" />)}
      {hover != null && (
        <g>
          <line
            x1={pts[hover][0]}
            y1={pad * 0.5}
            x2={pts[hover][0]}
            y2={h - pad}
            stroke="var(--linha)"
            strokeWidth="1.5"
          />
          <circle
            cx={pts[hover][0]}
            cy={pts[hover][1]}
            r="5"
            fill="#fff"
            stroke={color}
            strokeWidth="2.6"
          />
          <g
            transform={`translate(${Math.min(Math.max(pts[hover][0], 52), w - 52)}, ${Math.max(pts[hover][1] - 16, 14)})`}
          >
            <rect x="-50" y="-26" width="100" height="26" rx="7" fill="var(--ink)" />
            <text
              x="0"
              y="-8"
              textAnchor="middle"
              fill="#fff"
              fontSize="13"
              fontWeight="600"
              fontFamily="Inter"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {prefix}
              {Math.round(ys[hover] * scale).toLocaleString("pt-BR")}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}

/* ---------- Modal ---------- */
export function Modal({ open, onClose, children, maxWidth = 440 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  if (!open) return null;
  /* Portal no body: position:fixed dentro de ancestral animado por transform
     (.rise com fill-mode both) fica preso ao card em vez de cobrir o viewport. */
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,32,26,.42)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "grid",
        placeItems: "center",
        zIndex: 200,
        padding: 20,
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        role="dialog"
        aria-modal="true"
        style={{
          maxWidth,
          width: "100%",
          padding: 28,
          boxShadow: "var(--shadow-pop)",
          animation: "popIn .22s cubic-bezier(.2,.8,.3,1.1)",
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/* ---------- Avatar (iniciais) ---------- */
const AVATAR_TONES = ["#0E8C5A", "#1D6FE0", "#127C6E", "#9A6BD8", "#C9883A", "#D8635A"];
export function Avatar({ name = "", size = 40, tone = "", ring = false }) {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "?";
  const idx = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length;
  const bg = tone || AVATAR_TONES[idx];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        flex: "none",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: size * 0.4,
        letterSpacing: "-0.01em",
        boxShadow: ring ? "0 0 0 3px #fff, 0 0 0 4px var(--linha)" : "none",
      }}
    >
      {initials}
    </div>
  );
}
